import {AsyncPipe} from '@angular/common';
import {HttpClient, HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {ChangeDetectionStrategy, Component, inject} from '@angular/core';
import {catchError, map, Observable, tap, throwError} from 'rxjs';
import {FiscalRowComponent} from './fiscal-row/fiscal-row.component';
import {FiscalRowHeaderComponent} from './fiscal-row-header/fiscal-row-header.component';
import {FiscalLineChartComponent} from './fiscal-line-chart/fiscal-line-chart.component';
import {FiscalMonthEvents} from './fiscal-month-events/fiscal-month-events';
import {FiscalRateTable} from './fiscal-rate-table/fiscal-rate-table';
import {MarketSeriesManager, xForDate} from './market-series-manager';
import {
  ChartPoint,
  ChartSegment,
  DashboardData,
  EventBank,
  EventMarker,
  MarketWeek,
  Meeting,
  MonthColumn,
  MonthRateRow,
  PolicyBank,
  PolicyDataset,
  PolicyEvent,
  PolicyRateChange,
  PolicyRateMarker,
  RateAction,
  YearRow,
} from './policy-chart.models';

/** Federal fiscal year: October through September. */
const FISCAL_MONTHS = [10, 11, 12, 1, 2, 3, 4, 5, 6, 7, 8, 9];

/** Banks list alphabetically within a month, except the Fed, which always leads. */
const bankRank = (bank: EventBank): string => (bank === 'FED' ? '' : bank);

const MONTH_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    AsyncPipe,
    FiscalRowComponent,
    FiscalRowHeaderComponent,
    FiscalLineChartComponent,
    FiscalMonthEvents,
    FiscalRateTable,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent {
  private readonly http = inject(HttpClient);
  /** Owns the market feeds: what loads, and what is on screen. */
  readonly seriesManager = inject(MarketSeriesManager);
  private readonly requestUrl = '/api/policy-market';


  readonly dashboard$: Observable<DashboardData> = this.http
    .get<PolicyDataset>(this.requestUrl, {observe: 'response'})
    .pipe(
      tap((response: HttpResponse<PolicyDataset>) => {
        if (response.status !== 200) {
          console.error('Request failed', {
            requestUrl: this.requestUrl,
            statusCode: response.status,
            errorMessage: response.statusText || 'Unexpected HTTP response',
          });
          throw new Error(`Unexpected HTTP status ${response.status}`);
        }
      }),
      map((response) => {
        if (!response.body) {
          throw new Error('Firebase Function returned an empty response body');
        }
        const invalidWeek = response.body.weeks.find(
          (week) => !Number.isFinite(week.close) || week.close <= 0,
        );
        if (invalidWeek) {
          throw new Error(`Invalid weekly close for ${invalidWeek.weekStart}`);
        }
        const invalidGoldWeek = response.body.goldWeeks.find(
          (week) => !Number.isFinite(week.close) || week.close <= 0,
        );
        if (invalidGoldWeek) {
          throw new Error(`Invalid GLD weekly close for ${invalidGoldWeek.weekStart}`);
        }
        const invalidOilWeek = response.body.oilWeeks.find(
          (week) => !Number.isFinite(week.close) || week.close <= 0,
        );
        if (invalidOilWeek) {
          throw new Error(`Invalid USO weekly close for ${invalidOilWeek.weekStart}`);
        }
        const invalidJoblessWeek = response.body.unemploymentWeeks.find(
          (week) => !Number.isFinite(week.close) || week.close <= 0,
        );
        if (invalidJoblessWeek) {
          throw new Error(`Invalid unemployment reading for ${invalidJoblessWeek.weekStart}`);
        }
        const invalidDollarWeek = response.body.dollarWeeks.find(
          (week) => !Number.isFinite(week.close) || week.close <= 0,
        );
        if (invalidDollarWeek) {
          throw new Error(`Invalid dollar index weekly value for ${invalidDollarWeek.weekStart}`);
        }
        return response.body;
      }),
      map((dataset) => {
        const recentWeeks = dataset.weeks.slice(-104).reverse();
        return {
          years: this.buildYearRows(dataset),
          latest: recentWeeks[0],
          widest: [...recentWeeks].sort((a, b) => b.spreadPercent - a.spreadPercent)[0],
          meetingWeeks: recentWeeks.filter((week) => week.policyPhase === 'meeting_week').length,
          upcomingMeetings: dataset.meetings.filter(
            (meeting) =>
              !meeting.isNotationVote &&
              Date.parse(`${meeting.startDate}T23:59:59Z`) >= Date.now() &&
              this.fiscalYear(meeting.startDate) === this.fiscalYear(new Date().toISOString()),
          ).length,
        };
      }),
      catchError((error: HttpErrorResponse | Error) => {
        const statusCode = error instanceof HttpErrorResponse ? error.status : 0;
        const errorMessage = error instanceof HttpErrorResponse
          ? error.error?.message ?? error.message
          : error.message;
        console.error('Request failed', {
          requestUrl: this.requestUrl,
          statusCode,
          errorMessage,
        });
        return throwError(() => error);
      }),
    );

  /**
   * Every dated policy event in a fiscal year, ordered by date. The Fed and the
   * BOJ contribute their whole meeting calendars — a meeting matters even when it
   * changes nothing — while the BIS series only report actual rate changes.
   */
  private policyEvents(
    dataset: PolicyDataset,
    year: number,
    yearMeetings: Meeting[],
    yearRateActions: RateAction[],
  ): PolicyEvent[] {
    const fedEvents: PolicyEvent[] = yearMeetings
      .filter((meeting) => !meeting.isNotationVote)
      .map((meeting) => {
        const action = this.decidedAt(meeting, yearRateActions);
        const points = action
          ? (action.direction === 'increase' ? 1 : -1) * (action.basisPoints / 100)
          : undefined;
        return {
          bank: 'FED' as const,
          date: meeting.startDate,
          dateLabel: this.dateLabel(meeting.startDate),
          changeLabel: points === undefined ? undefined : this.pointsLabel(points),
          changePoints: points,
          isMeeting: true,
          future: Date.parse(`${meeting.endDate}T23:59:59Z`) >= Date.now(),
          hasProjections: meeting.hasEconomicProjections,
          emergency: meeting.isEmergency,
        };
      });
    const bojEvents: PolicyEvent[] = dataset.bojMeetings
      .filter((meeting) => this.fiscalYear(meeting.startDate) === year)
      .map((meeting) => {
        const action = dataset.bojRateActions.find(
          (candidate) => candidate.date >= meeting.startDate && candidate.date <= meeting.endDate,
        );
        return {
          bank: 'BOJ' as const,
          date: meeting.startDate,
          dateLabel: this.dateLabel(meeting.startDate),
          changeLabel: action ? this.changeLabel(action) : undefined,
          changePoints: action ? this.changePoints(action) : undefined,
          isMeeting: true,
          future: Date.parse(`${meeting.endDate}T23:59:59Z`) >= Date.now(),
          hasProjections: false,
          emergency: false,
        };
      });
    const bisEvents: PolicyEvent[] = this.policyChangesByBank(dataset).flatMap(
      ({bank, changes}) => changes
        .filter((change) => this.fiscalYear(change.date) === year)
        .map((change) => ({
          bank,
          date: change.date,
          dateLabel: this.dateLabel(change.date),
          changeLabel: this.changeLabel(change),
          changePoints: this.changePoints(change),
          isMeeting: false,
          future: false,
          hasProjections: false,
          emergency: false,
        })),
    );
    return [...fedEvents, ...bojEvents, ...bisEvents]
      .sort((first, second) => first.date.localeCompare(second.date));
  }

  private changeLabel(change: PolicyRateChange): string {
    return this.pointsLabel(this.changePoints(change));
  }

  private changePoints(change: PolicyRateChange): number {
    return (change.direction === 'increase' ? 1 : -1) * change.percentagePoints;
  }

  private pointsLabel(points: number): string {
    return `${points >= 0 ? '↑' : '↓'}${Math.abs(points)}%`;
  }

  private dateLabel(isoDate: string): string {
    const month = MONTH_LABELS[Number(isoDate.slice(5, 7)) - 1];
    return `${month} ${isoDate.slice(8, 10)}`;
  }

  /**
   * Each bank's rate as it stood at the end of every month of the fiscal year.
   * A bank only reports when it moves, so the last change on or before a month
   * carries forward; before a bank's first recorded change the rate it moved
   * away from stands in.
   */
  private buildRateTable(dataset: PolicyDataset, year: number): MonthRateRow[] {
    const prefixes = FISCAL_MONTHS.map((month) => {
      const calendarYear = month >= 10 ? year - 1 : year;
      return `${calendarYear}-${String(month).padStart(2, '0')}`;
    });
    // The Fed publishes a target range, and no range is on record before the
    // first action in the window, so early months stay blank.
    const fedLevels = prefixes.map((prefix) => {
      const prior = dataset.rateActions.filter((action) => action.date.slice(0, 7) <= prefix);
      return prior.length ? prior[prior.length - 1].level : null;
    });
    const fedRow: MonthRateRow = {
      bank: 'FED',
      levels: fedLevels.map((level) => (level === null ? null : `${level}%`)),
      values: fedLevels.map((level) => (level === null ? null : this.rangeMidpoint(level))),
    };
    const numericRows = [
      ...this.policyChangesByBank(dataset).map(
        ({bank, changes}) => [bank, changes] as [EventBank, PolicyRateChange[]],
      ),
      ['BOJ', dataset.bojRateActions] as [EventBank, PolicyRateChange[]],
    ]
      .sort(([first], [second]) => first.localeCompare(second))
      .map(([bank, changes]) => {
        const values = prefixes.map((prefix) => {
          const prior = changes.filter((change) => change.date.slice(0, 7) <= prefix);
          if (prior.length) return prior[prior.length - 1].newRate;
          return changes.length ? changes[0].previousRate : null;
        });
        return {
          bank,
          levels: values.map((value) => (value === null ? null : `${value.toFixed(2)}%`)),
          values,
        };
      });

    return [fedRow, ...numericRows];
  }

  /** '3.50-3.75' → 3.625, so a target range can be totalled with single rates. */
  private rangeMidpoint(level: string): number | null {
    const parts = level.split('-').map((part) => Number(part.trim()));
    if (!parts.length || parts.some((part) => !Number.isFinite(part))) return null;
    return parts.reduce((total, part) => total + part, 0) / parts.length;
  }

  /**
   * Every BIS policy series the chart can overlay, tagged with its bank. Driven by
   * the registry rather than listed here, so a bank is added in one place.
   */
  private policyChangesByBank(dataset: PolicyDataset): {bank: PolicyBank; changes: PolicyRateChange[]}[] {
    return this.seriesManager.bisBanks().map((bank) => ({
      bank: bank.key,
      changes: dataset.policyRateChanges[bank.area] ?? [],
    }));
  }


  private buildYearRows(dataset: PolicyDataset): YearRow[] {
    const currentFiscalYear = this.fiscalYear(new Date().toISOString());
    const years = this.seriesManager.fiscalYears(dataset, currentFiscalYear);

    return years.map((year) => {
      const start = Date.UTC(year - 1, 9, 1);
      const end = Date.UTC(year, 9, 1);
      const yearMeetings = dataset.meetings.filter(
        (meeting) => this.fiscalYear(meeting.startDate) === year,
      );
      const yearRateActions = dataset.rateActions.filter(
        (action) => this.fiscalYear(action.date) === year,
      );
      const fedMarkers = yearMeetings.map((meeting) => ({
        x: xForDate(meeting.startDate, start, end),
        startDate: meeting.startDate,
        hasProjections: meeting.hasEconomicProjections,
        future: Date.parse(`${meeting.startDate}T23:59:59Z`) >= Date.now(),
        emergency: meeting.isEmergency,
        rateLabel: this.decidedAt(meeting, yearRateActions)?.level,
      }));
      const bojMarkers = dataset.bojMeetings
        .filter((meeting) => this.fiscalYear(meeting.startDate) === year)
        .map((meeting) => {
          const action = dataset.bojRateActions.find(
            (candidate) => candidate.date >= meeting.startDate && candidate.date <= meeting.endDate,
          );
          return {
            x: xForDate(meeting.startDate, start, end),
            startDate: meeting.startDate,
            future: Date.parse(`${meeting.startDate}T23:59:59Z`) >= Date.now(),
            rateLabel: action ? `${action.newRate.toFixed(2)}%` : undefined,
          };
        });
      const rateActionMarkers = yearRateActions.map((action) => ({
        ...action,
        x: xForDate(action.date, start, end),
      }));
      const policyMarkers: PolicyRateMarker[] = this.policyChangesByBank(dataset).flatMap(
        ({bank, changes}) => changes
          .filter((change) => this.fiscalYear(change.date) === year)
          .map((change) => ({...change, bank, x: xForDate(change.date, start, end)})),
      );
      const events = this.policyEvents(dataset, year, yearMeetings, yearRateActions);
      const eventMarkers: EventMarker[] = events.map((event) => ({
        x: xForDate(event.date, start, end),
        bank: event.bank,
        date: event.date,
        emergency: event.emergency,
        future: Date.parse(`${event.date}T23:59:59Z`) >= Date.now(),
        month: Number(event.date.slice(5, 7)),
        changed: event.changePoints !== undefined && event.changePoints !== 0,
      }));
      const monthColumns: MonthColumn[] = FISCAL_MONTHS.map((month) => {
        const calendarYear = month >= 10 ? year - 1 : year;
        const prefix = `${calendarYear}-${String(month).padStart(2, '0')}`;
        return {
          month,
          label: MONTH_LABELS[month - 1],
          events: events
            .filter((event) => event.date.startsWith(prefix))
            .sort((first, second) =>
              bankRank(first.bank).localeCompare(bankRank(second.bank)) ||
              first.date.localeCompare(second.date)),
        };
      });
      const series = this.seriesManager.load(dataset, year, yearMeetings);
      const rbaInflationTarget = dataset.australiaInflationTargets.find(
        (target) => target.year === year,
      );
      const rbaEggQuarters = dataset.australiaEggPriceIndex.observations.filter(
        (observation) => {
          const [calendarYear, quarterLabel] = observation.period.split('-Q');
          const quarterEndMonth = Number(quarterLabel) * 3;
          return this.fiscalYear(
            `${calendarYear}-${String(quarterEndMonth).padStart(2, '0')}-01`,
          ) === year;
        },
      );
      const rbaEggPriceIndex = rbaEggQuarters.length
        ? {
          value: Number((rbaEggQuarters.reduce(
            (total, observation) => total + observation.value,
            0,
          ) / rbaEggQuarters.length).toFixed(2)),
          quartersReported: rbaEggQuarters.length,
          completeFiscalYear: rbaEggQuarters.length === 4,
          referenceBasePeriod: dataset.australiaEggPriceIndex.referenceBasePeriod,
        }
        : undefined;
      const rbaHouseQuarters = dataset.australiaHousePriceIndex.observations.filter(
        (observation) => {
          const [calendarYear, quarterLabel] = observation.period.split('-Q');
          const quarterEndMonth = Number(quarterLabel) * 3;
          return this.fiscalYear(
            `${calendarYear}-${String(quarterEndMonth).padStart(2, '0')}-01`,
          ) === year;
        },
      );
      const rbaHousePriceIndex = rbaHouseQuarters.length
        ? {
          value: Number((rbaHouseQuarters.reduce(
            (total, observation) => total + observation.value,
            0,
          ) / rbaHouseQuarters.length).toFixed(2)),
          quartersReported: rbaHouseQuarters.length,
          completeFiscalYear: rbaHouseQuarters.length === 4,
          referenceBasePeriod: dataset.australiaHousePriceIndex.referenceBasePeriod,
        }
        : undefined;
      const bcbInflationTarget = dataset.brazilInflationTargets.find(
        (target) => target.year === year,
      );
      const bcbEggMonths = dataset.brazilEggPriceIndex.observations.filter(
        (observation) => this.fiscalYear(`${observation.period}-01`) === year,
      );
      const bcbEggPriceIndex = bcbEggMonths.length
        ? {
          value: Number((bcbEggMonths.reduce(
            (total, observation) => total + observation.value,
            0,
          ) / bcbEggMonths.length).toFixed(2)),
          monthsReported: bcbEggMonths.length,
          completeFiscalYear: bcbEggMonths.length === 12,
          referenceBasePeriod: dataset.brazilEggPriceIndex.referenceBasePeriod,
        }
        : undefined;
      const bcbHouseQuarters = dataset.brazilHousePriceIndex.observations.filter(
        (observation) => {
          const [calendarYear, quarterLabel] = observation.period.split('-Q');
          const quarterEndMonth = Number(quarterLabel) * 3;
          return this.fiscalYear(
            `${calendarYear}-${String(quarterEndMonth).padStart(2, '0')}-01`,
          ) === year;
        },
      );
      const bcbHousePriceIndex = bcbHouseQuarters.length
        ? {
          value: Number((bcbHouseQuarters.reduce(
            (total, observation) => total + observation.value,
            0,
          ) / bcbHouseQuarters.length).toFixed(2)),
          quartersReported: bcbHouseQuarters.length,
          completeFiscalYear: bcbHouseQuarters.length === 4,
          referenceBasePeriod: dataset.brazilHousePriceIndex.referenceBasePeriod,
        }
        : undefined;
      return {
        year,
        series,
        fedMarkers,
        bojMarkers,
        rateActionMarkers,
        policyMarkers,
        eventMarkers,
        monthColumns,
        rateTable: this.buildRateTable(dataset, year),
        events,
        upcomingCount: year === currentFiscalYear
          ? yearMeetings.filter(
            (meeting) =>
              !meeting.isNotationVote &&
              Date.parse(`${meeting.startDate}T23:59:59Z`) >= Date.now(),
          ).length
          : 0,
        months: this.seriesManager.loadMonths(dataset, series, year),
        rbaIndicators: rbaInflationTarget || rbaEggPriceIndex || rbaHousePriceIndex
          ? {
            inflationTarget: rbaInflationTarget,
            eggPriceIndex: rbaEggPriceIndex,
            housePriceIndex: rbaHousePriceIndex,
          }
          : undefined,
        bcbIndicators: bcbInflationTarget || bcbEggPriceIndex || bcbHousePriceIndex
          ? {
            inflationTarget: bcbInflationTarget,
            eggPriceIndex: bcbEggPriceIndex,
            housePriceIndex: bcbHousePriceIndex,
          }
          : undefined,
      };
    });
  }

  /**
   * The rate action a meeting produced, if any. Decisions are published on the
   * closing day, but implementation notes can land a couple of days later.
   */
  private decidedAt(meeting: Meeting, actions: RateAction[]): RateAction | undefined {
    const matchStart = Date.parse(`${meeting.startDate}T00:00:00Z`);
    const matchEnd = Date.parse(`${meeting.endDate}T00:00:00Z`) + 2 * 86_400_000;
    return actions.find((action) => {
      const actionTime = Date.parse(`${action.date}T00:00:00Z`);
      return actionTime >= matchStart && actionTime <= matchEnd;
    });
  }

  private fiscalYear(isoDate: string): number {
    const year = Number(isoDate.slice(0, 4));
    const month = Number(isoDate.slice(5, 7));
    return month >= 10 ? year + 1 : year;
  }
}
